import React, { useState } from 'react';
import { 
  Avatar, 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Badge, 
  Tooltip,
  Divider,
  Chip
} from '@mui/material';
import { 
  faBell, 
  faCog, 
  faSignOutAlt, 
  faUser, 
  faHome,
  faGraduationCap,
  faBook,
  faTrophy,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

const MembroTopo: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/inicio/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
    handleMenuClose();
  };

  const navigationItems = [
    { label: 'Início', path: '/membro', icon: faHome },
    { label: 'Lições', path: '/licao', icon: faGraduationCap },
    { label: 'Declaração', path: '/declaracao', icon: faBook },
    { label: 'Rank', path: '/rank', icon: faTrophy },
    { label: 'Carteirinha', path: '/carteirinha', icon: faIdCard }
  ];

  const notifications = [
    { id: 1, message: 'Nova lição disponível: "Os Sete Mandamentos"', time: '2 min atrás', read: false },
    { id: 2, message: 'Sua declaração foi aprovada!', time: '1 hora atrás', read: false },
    { id: 3, message: 'Você subiu no ranking! Agora está em 5º lugar', time: '3 horas atrás', read: true },
    { id: 4, message: 'Lembrete: Complete sua lição semanal', time: '1 dia atrás', read: true }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header className="membro-topo" style={{
      background: 'linear-gradient(135deg, #225477 0%, #4688ae 100%)',
      padding: '0 24px',
      height: '70px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      {/* Logo and Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div className="logo" style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/assets/images/logotipo-horizontal.png" 
            alt="Logo" 
            style={{ height: '40px', filter: 'brightness(0) invert(1)' }}
          />
        </div>
        
        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          {navigationItems.map((item) => (
            <Tooltip key={item.path} title={item.label}>
              <IconButton
                onClick={() => navigate(item.path)}
                sx={{
                  color: 'white',
                  backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'transparent',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                <FontAwesomeIcon icon={item.icon} style={{ fontSize: '1.1rem' }} />
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                  {item.label}
                </Typography>
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* User Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Notifications */}
        <Tooltip title="Notificações">
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              width: 45,
              height: 45,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <FontAwesomeIcon icon={faBell} style={{ fontSize: '1.2rem' }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
              {user?.nome || 'Usuário'}
            </Typography>
            <Chip 
              label="Membro Ativo" 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white', 
                fontSize: '0.7rem',
                height: 20
              }} 
            />
          </Box>
          
          <Tooltip title="Menu do usuário">
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                p: 0,
                border: '3px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  border: '3px solid rgba(255,255,255,0.5)'
                }
              }}
            >
              <Avatar 
                src={user?.perfil || '/assets/images/perfil.png'} 
                alt="Perfil"
                sx={{ width: 45, height: 45 }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { navigate('/membro'); handleMenuClose(); }}>
          <FontAwesomeIcon icon={faUser} style={{ marginRight: 12, color: '#225477' }} />
          Meu Perfil
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <FontAwesomeIcon icon={faCog} style={{ marginRight: 12, color: '#225477' }} />
          Configurações
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: '#d32f2f' }}>
          <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: 12 }} />
          Sair
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 350,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ color: '#225477', fontWeight: 600 }}>
            Notificações
          </Typography>
        </Box>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id} 
              sx={{ 
                p: 2, 
                backgroundColor: notification.read ? 'transparent' : '#f3f8ff',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.time}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhuma notificação
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </header>
  );
};

export default MembroTopo; 